import { Mail, Phone, MapPin, Linkedin, Globe, Github, Calendar, Award } from 'lucide-react'
import { getColorClasses } from '@/lib/templateColors'

interface ResumeTemplateProps {
  data: any
  language?: string
  labels: any
  color?: string
}


export default function ExecutiveTemplate({ data, labels, color = 'blue' }: ResumeTemplateProps) {
  const { personalInfo, summary, experience, education, skills, projects, certifications } = data
  const colors = getColorClasses(color)

  return (

      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        <div id="resume-preview" className="bg-white" style={{ width: '8.5in', minHeight: '11in', margin: '0 auto' }}>
          {/* Elegant Header with Thin Top Border */}
          <div className={`border-t-8 ${colors.accentBorder}`}>
            <div className="p-8 pb-6">
              <h1 className={`text-5xl font-bold ${colors.sectionTitle} mb-2 tracking-tight`}>
                {personalInfo.fullName || 'YOUR NAME'}
              </h1>
              <div className="text-lg text-gray-600 mb-4">{personalInfo.email}</div>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600">
                {personalInfo.phone && <span>{personalInfo.phone}</span>}
                {personalInfo.address && <span>{personalInfo.address}</span>}
                {personalInfo.linkedin && <span>{personalInfo.linkedin}</span>}
              </div>
            </div>
            <div className="h-px bg-gray-300 mx-8"></div>
          </div>

          <div className="p-8 pt-6">
            {/* Executive Summary */}
            {summary && (
              <div className="mb-6">
                <h2 className={`text-sm font-bold ${colors.sectionTitle} uppercase tracking-wider mb-3 pb-2 border-b ${colors.sectionBorder}`}>
                  {labels.professionalSummary}
                </h2>
                <p className="text-gray-700 leading-relaxed">{summary}</p>
              </div>
            )}

            {/* Experience */}
            {experience && experience.length > 0 && (
              <div className="mb-6">
                <h2 className={`text-sm font-bold ${colors.sectionTitle} uppercase tracking-wider mb-3 pb-2 border-b ${colors.sectionBorder}`}>
                  {labels.workExperience}
                </h2>
                <div className="space-y-4">
                  {experience.map((exp: any) => (
                    <div key={exp.id}>
                      <div className="flex justify-between items-baseline mb-1">
                        <h3 className={`text-base font-bold ${colors.sectionTitle}`}>{exp.jobTitle}</h3>
                        <span className="text-sm text-gray-600">{exp.startDate} - {exp.endDate || labels.present}</span>
                      </div>
                      <div className={`text-sm font-semibold ${colors.accentText} mb-2`}>{exp.company}</div>
                      <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{exp.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Two Column Layout for Skills and Education */}
            <div className="grid grid-cols-2 gap-6">
              {/* Skills */}
              {skills && skills.length > 0 && (
                <div>
                  <h2 className={`text-sm font-bold ${colors.sectionTitle} uppercase tracking-wider mb-3 pb-2 border-b ${colors.sectionBorder}`}>
                    {labels.skills}
                  </h2>
                  <div className="space-y-2">
                    {skills.map((skillGroup: any, idx: number) => (
                      <div key={idx}>
                        <div className={`text-sm font-semibold ${colors.accentText} mb-1`}>{skillGroup.category}</div>
                        <div className="flex flex-wrap gap-2">
                          {skillGroup.skills?.map((skill: string, i: number) => (
                            <span key={i} className="text-xs text-gray-600">{skill}{i < skillGroup.skills.length - 1 ? ',' : ''}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Education */}
              {education && education.length > 0 && (
                <div>
                  <h2 className={`text-sm font-bold ${colors.sectionTitle} uppercase tracking-wider mb-3 pb-2 border-b ${colors.sectionBorder}`}>
                    {labels.education}
                  </h2>
                  <div className="space-y-3">
                    {education.map((edu: any) => (
                      <div key={edu.id}>
                        <div className={`text-sm font-bold ${colors.sectionTitle}`}>{edu.degree}</div>
                        <div className={`text-sm ${colors.accentText}`}>{edu.institution}</div>
                        <div className="text-xs text-gray-600">{edu.graduationDate}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
  )
}
